import { useRef, useState, type ReactElement } from "react";
import { ImageOff, Upload } from "lucide-react";
import type { ImageParams } from "@playbookai/widget-schemas";
import { Button } from "../../atoms/Button/Button.js";
import type { WidgetBodyProps } from "../types.js";
import { useFieldEditSession } from "../field-edit-session.js";

/**
 * Image card body — Hoshin canvas Yjs widget (PB-234). Two states:
 *   - EDIT form (default for a fresh card): an "Upload Image" affordance +
 *     alt-text field, committed with Cancel/Save.
 *   - DISPLAY: the image (`object-contain`), with a hover "Edit" affordance.
 *
 * Legacy Yjs cards accept size-capped `data:image/` uploads. Entity-backed
 * `imageTile` passes `hostedOnly`, accepts HTTPS object-storage URLs only, and
 * never puts bytes or a base64 copy in widget params/Yjs. Card frame and drag
 * header come from the adapter.
 */
function isHostedHttpsUrl(value: string): boolean {
  if (!/^https:\/\/[^/?#]+(?:[/?#]|$)/i.test(value)) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" && parsed.hostname.length > 0;
  } catch {
    return false;
  }
}

function isDisplayableImage(url: string, hostedOnly: boolean): boolean {
  return hostedOnly ? isHostedHttpsUrl(url) : url.startsWith("data:image/");
}

/** Max uploaded-image size (bytes). Keeps embedded data URIs from bloating the
 *  shared Yjs doc; larger images should be referenced by URL. The resulting
 *  data-URI length must stay within `ImageSrcText` (widget-schemas) or a saved
 *  upload would fail `paramsFromY` re-validation and blank the card — the
 *  interactions test asserts this coupling. */
export const MAX_UPLOAD_BYTES = 512 * 1024; // 512 KB

/** Max character length of an inline `data:` URI, derived from
 *  `MAX_UPLOAD_BYTES` (base64 inflates ~4/3, plus the `data:…;base64,` prefix).
 *  Enforced on save so a data URI pasted/typed into the URL field can't smuggle
 *  a payload past the upload cap into the shared Y.Doc. */
export const MAX_DATA_URI_CHARS = Math.ceil(MAX_UPLOAD_BYTES / 3) * 4 + 64;

export function ImageWidget({
  data,
  onChange,
  readOnly,
  hostedOnly = false,
}: WidgetBodyProps<ImageParams> & {
  readonly hostedOnly?: boolean;
}): ReactElement {
  const hasImage = isDisplayableImage(data.url, hostedOnly);
  const [editing, setEditing] = useState(!hasImage);
  const [draftUrl, setDraftUrl] = useState(data.url);
  const [draftAlt, setDraftAlt] = useState(data.alt);
  const [failed, setFailed] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [syncedData, setSyncedData] = useState({
    url: data.url,
    alt: data.alt,
  });
  const fileRef = useRef<HTMLInputElement>(null);
  /*
   * PB-4923 AC5 — `imageTile`'s two mapped leaves. The claim covers the DRAFT
   * window rather than the save, which is the point: this form re-syncs from
   * `data` mid-edit (see below), so a change landing while somebody is typing
   * a URL discards what they typed. The legacy Yjs `image` kind renders the
   * same body without a session provider, where both hooks are inert.
   */
  const urlLease = useFieldEditSession("url");
  const altLease = useFieldEditSession("alt");

  // Re-sync the draft from `data` if a remote collaborator changes it mid-edit,
  // so a later save can't silently clobber their update with a stale snapshot.
  // Done during render (React's "you might not need an effect" pattern) rather
  // than in a useEffect, which the DS forbids (cascading renders). Only fires on
  // a genuine `data` change — local draft typing/uploads (data unchanged) are
  // preserved; no-op today as the canvas is single-user until PB-228.
  if (editing && (syncedData.url !== data.url || syncedData.alt !== data.alt)) {
    setSyncedData({ url: data.url, alt: data.alt });
    setDraftUrl(data.url);
    setDraftAlt(data.alt);
  }

  const openEditor = (): void => {
    setDraftUrl(data.url);
    setDraftAlt(data.alt);
    setUploadError(null);
    setEditing(true);
  };
  const save = (): void => {
    const url = draftUrl.trim();
    // Uploads only: accept an uploaded data:image/ URI (or empty to clear).
    // External http(s) URLs are CSP-blocked at render, so reject them here with
    // a clear message rather than saving a source that can never display.
    if (hostedOnly && url && !isHostedHttpsUrl(url)) {
      setUploadError(
        "Use the hosted HTTPS URL returned by the image upload service.",
      );
      return;
    }
    if (!hostedOnly && url && !url.startsWith("data:image/")) {
      setUploadError(
        "External image URLs aren't supported yet — upload an image instead.",
      );
      return;
    }
    // Belt-and-suspenders: a data URI over the upload cap (e.g. pasted) must not
    // reach the shared Y.Doc.
    if (url.length > MAX_DATA_URI_CHARS) {
      setUploadError("Image is too large (max 512 KB).");
      return;
    }
    setUploadError(null);
    onChange({ url, alt: draftAlt.trim() });
    setFailed(false);
    setEditing(false);
  };
  const cancel = (): void => {
    setDraftUrl(data.url);
    setDraftAlt(data.alt);
    setEditing(false);
  };
  const onFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError("Image is too large (max 512 KB). Try a smaller image.");
      return;
    }
    setUploadError(null);
    const reader = new FileReader();
    reader.onload = (): void => {
      if (typeof reader.result === "string") setDraftUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  if (editing && !readOnly) {
    return (
      <div className="flex h-full w-full flex-col p-3">
        <span className="text-dense-2 uppercase tracking-wider text-pb-text-muted">
          Image
        </span>
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a 26px field with 11px text and a 4px corner; Input size="sm" is a 28px, 12px-text field with the 8px corner, and no approved look row lists the change. */}
        <input
          value={draftUrl}
          {...urlLease}
          onChange={(e) => setDraftUrl(e.target.value)}
          placeholder={hostedOnly ? "https://…" : "Upload an image below"}
          aria-label="Image URL"
          className="mt-1 w-full rounded border border-pb-control-border bg-pb-card px-2 py-1 text-dense-1 text-pb-text placeholder:text-pb-text-muted focus-frame"
        />
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a 26px field with 11px text and a 4px corner; Input size="sm" is a 28px, 12px-text field with the 8px corner, and no approved look row lists the change. */}
        <input
          value={draftAlt}
          {...altLease}
          onChange={(e) => setDraftAlt(e.target.value)}
          placeholder="Alt text (optional)"
          aria-label="Image alt text"
          maxLength={255}
          className="mt-2 w-full rounded border border-pb-control-border bg-pb-card px-2 py-1 text-dense-1 text-pb-text placeholder:text-pb-text-muted focus-frame"
        />
        {hostedOnly ? null : (
          <Button
            variant="dashed"
            size="sm"
            onClick={() => fileRef.current?.click()}
            className="mt-2 w-full"
          >
            <Upload className="h-3.5 w-3.5" aria-hidden /> Upload Image
          </Button>
        )}
        {uploadError ? (
          <span role="alert" className="mt-1 text-dense-1 text-pb-red-text">
            {uploadError}
          </span>
        ) : null}
        {hostedOnly ? null : (
          // eslint-disable-next-line ds/no-hand-built -- hidden native file picker behind the upload button; the design system has no picker-only part
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onFile}
            className="hidden"
            tabIndex={-1}
            aria-hidden
          />
        )}
        <div className="mt-auto flex items-center justify-end gap-2 pt-2">
          <Button size="sm" variant="ghost" onClick={cancel}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" onClick={save}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group/img relative flex h-full w-full items-center justify-center bg-pb-card-elevated">
      {hasImage && !failed ? (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onError is load-failure handling, not a user interaction; needed to show the fallback
        <img
          src={data.url}
          alt={data.alt}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
          className="max-h-full max-w-full object-contain"
        />
      ) : (
        <div className="flex flex-col items-center gap-1 px-3 text-center text-pb-text-muted">
          <ImageOff className="h-6 w-6" aria-hidden />
          <span className="text-dense-1">
            {failed ? "Image failed to load" : "No image"}
          </span>
        </div>
      )}
      {readOnly ? null : (
        // eslint-disable-next-line ds/no-hand-built -- kept: a 10px-text overlay chip on a translucent card fill that appears on hover; no Button size or variant paints it, and no approved look row lists the change.
        <button
          type="button"
          onClick={openEditor}
          className="absolute end-2 top-2 rounded bg-pb-card/90 px-2 py-1 text-dense-2 font-medium text-pb-text-muted opacity-0 shadow-sm transition-opacity hover:text-pb-text group-hover/img:opacity-100 focus-visible:opacity-100 focus-ring"
        >
          Edit
        </button>
      )}
    </div>
  );
}
